import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, FormControlLabel, Icon, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import ProgressBar from '../controls/ProgressBar';
import FilterCategoryMultiSelect from '../controls/FilterCategoryMultiSelect';

import 'dayjs/locale/nl'; // Import Dutch locale
import dayjs from 'dayjs';
// Set default locale globally
dayjs.locale('nl');

const MIN_DATE = dayjs('1970-01-01');
const MAX_DATE = dayjs('2500-12-31');

interface FilterPlannenProps {
  onChange: (key: string, value: any, isDelete?: boolean) => void;
  filterData?: any;
  selectedSearch: string;
  startDateFrom: string;
  startDateTo: string;
  endDateFrom: string;
  endDateTo: string;
  includeZonderEinddatum: boolean;
  selectedFilters: Record<string, any[]>;
  sidePanel?: boolean;
  onClose?: () => void;
}

const FilterPlannen: React.FC<FilterPlannenProps> = ({
  onChange,
  filterData,
  selectedSearch,
  startDateFrom,
  startDateTo,
  endDateFrom,
  endDateTo,
  includeZonderEinddatum,
  selectedFilters,
  sidePanel = false,
  onClose,
}) => {
  const [loading] = useState<boolean>(false);
  const [filterOptionOpen, setFilterOptionOpen] = useState(false);
  const [errorStartDateFrom, setErrorStartDateFrom] = useState<string | null>(null);
  const [errorStartDateTo, setErrorStartDateTo] = useState<string | null>(null);
  const [errorEndDateFrom, setErrorEndDateFrom] = useState<string | null>(null);
  const [errorEndDateTo, setErrorEndDateTo] = useState<string | null>(null);

  const [disciplines, setDisciplines] = useState<any[]>([]);
  const [statusses, setStatusses] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [organisaties, setOrganisaties] = useState<any[]>([]);
  const [beoordelingStatusses, setBeoordeling] = useState<any[]>([]);
  const [gemeentes, setGemeentes] = useState<any[]>([]);

  // Ref to prevent fetching data twice in Strict Mode
  const hasFetchedData = useRef(false);

  // useEffect(() => {
  //   // Ensure the fetch happens only once
  //   if (!hasFetchedData.current) {
  //     setFilterData(filterData);
  //     hasFetchedData.current = true; // Mark as fetched
  //   }
  // }, []); // Empty dependency array ensures this runs once when the component mounts

  useEffect(() => {
    // Ensure the fetch happens only once
    if (!hasFetchedData.current && filterData && Object.keys(filterData).length > 0) {
      setFilterData(filterData);
      hasFetchedData.current = true; // Mark as fetched
    }
  }, [filterData]); // Empty dependency array ensures this runs once when the component mounts

  const setFilterData = (data: any) => {
    const filters = data;
    if (filters.discipline) {
      setDisciplines(filters.discipline);
    }

    if (filters.planStatus) {
      setStatusses(filters.planStatus);
    }

    if (filters.planType) {
      setTypes(filters.planType);
    }

    if (filters.infplBeoordeling) {
      setBeoordeling(filters.infplBeoordeling);
    }

    if (filters.organizations) {
      setOrganisaties(filters.organizations);
    }

    if (filters.gemeentes) {
      setGemeentes(filters.gemeentes);
    }
  };
  
  const onChangeSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    //console.log("onChangeSearchInput:", value);
    onChange('search', value);

    // setSelectedSearch(searchValue);
  };

  const onChangeDate = (key: string, value: any) => {
    onChange(key, value);
  };

  const handleDateChange = (key: string) => (date: any) => {
    if (date === null) { onChangeDate(key, ''); return; }
    if (!date.isValid()) return;
    onChangeDate(key, date.format('YYYY-MM-DD'));
  };

  const handleMultiSelectChange = (key: string, selectedValues: any[]) => {
    onChange(key, selectedValues);
  };

  const hasActiveFilters =
    Object.values(selectedFilters).some(arr => arr.length > 0) ||
    !!startDateFrom || !!startDateTo ||
    !!endDateFrom || !!endDateTo ||
    includeZonderEinddatum;

  if (sidePanel) {
    return (
      <div className="filter-side-panel">
        <div className="filter-side-panel-header">
          <span>Uitgebreid zoeken</span>
          <div className="filter-side-panel-header-actions">
            {hasActiveFilters && (
              <Tooltip title="Verwijder alle filters">
                <IconButton size="small" onClick={() => onChange('all', '', true)}>
                  <Icon>delete</Icon>
                </IconButton>
              </Tooltip>
            )}
            {onClose && (
              <Tooltip title="Sluit filter">
                <IconButton size="small" onClick={onClose}>
                  <Icon>close</Icon>
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>

        <div className="filter-side-panel-body">
          <div className="filter-section">
            <Typography variant="overline" color="text.secondary">Startdatum</Typography>
            <DatePicker
              sx={{ width: '100%' }}
              label="Van"
              value={startDateFrom ? dayjs(startDateFrom) : null}
              onChange={handleDateChange('startDateFrom')}
              onError={(newError) => setErrorStartDateFrom(newError)}
              format="DD/MM/YYYY"
              minDate={MIN_DATE}
              maxDate={MAX_DATE}
              slotProps={{
                field: { clearable: true },
                textField: {
                  variant: 'filled',
                  helperText:
                    errorStartDateFrom === 'minDate'
                      ? 'Datum mag niet voor 01/01/1970 zijn.'
                      : errorStartDateFrom === 'maxDate'
                      ? 'Datum mag niet na 31/12/2500 zijn.'
                      : errorStartDateFrom === 'invalidDate'
                      ? 'Ongeldige datum.'
                      : '',
                  FormHelperTextProps: { sx: { color: 'error.main' } },
                },
              }}
            />
            <DatePicker
              sx={{ width: '100%', mt: 1 }}
              label="Tot en met"
              value={startDateTo ? dayjs(startDateTo) : null}
              onChange={handleDateChange('startDateTo')}
              onError={(newError) => setErrorStartDateTo(newError)}
              format="DD/MM/YYYY"
              minDate={MIN_DATE}
              maxDate={MAX_DATE}
              slotProps={{
                field: { clearable: true },
                textField: {
                  variant: 'filled',
                  helperText:
                    errorStartDateTo === 'minDate'
                      ? 'Datum mag niet voor 01/01/1970 zijn.'
                      : errorStartDateTo === 'maxDate'
                      ? 'Datum mag niet na 31/12/2500 zijn.'
                      : errorStartDateTo === 'invalidDate'
                      ? 'Ongeldige datum.'
                      : '',
                  FormHelperTextProps: { sx: { color: 'error.main' } },
                },
              }}
            />
          </div>

          <div className="filter-section">
            <Typography variant="overline" color="text.secondary">Einddatum</Typography>
            <DatePicker
              sx={{ width: '100%' }}
              label="Van"
              value={endDateFrom ? dayjs(endDateFrom) : null}
              onChange={handleDateChange('endDateFrom')}
              onError={(newError) => setErrorEndDateFrom(newError)}
              format="DD/MM/YYYY"
              minDate={MIN_DATE}
              maxDate={MAX_DATE}
              slotProps={{
                field: { clearable: true },
                textField: {
                  variant: 'filled',
                  helperText:
                    errorEndDateFrom === 'minDate'
                      ? 'Datum mag niet voor 01/01/1970 zijn.'
                      : errorEndDateFrom === 'maxDate'
                      ? 'Datum mag niet na 31/12/2500 zijn.'
                      : errorEndDateFrom === 'invalidDate'
                      ? 'Ongeldige datum.'
                      : '',
                  FormHelperTextProps: { sx: { color: 'error.main' } },
                },
              }}
            />
            <DatePicker
              sx={{ width: '100%', mt: 1 }}
              label="Tot en met"
              value={endDateTo ? dayjs(endDateTo) : null}
              onChange={handleDateChange('endDateTo')}
              onError={(newError) => setErrorEndDateTo(newError)}
              format="DD/MM/YYYY"
              minDate={MIN_DATE}
              maxDate={MAX_DATE}
              slotProps={{
                field: { clearable: true },
                textField: {
                  variant: 'filled',
                  helperText:
                    errorEndDateTo === 'minDate'
                      ? 'Datum mag niet voor 01/01/1970 zijn.'
                      : errorEndDateTo === 'maxDate'
                      ? 'Datum mag niet na 31/12/2500 zijn.'
                      : errorEndDateTo === 'invalidDate'
                      ? 'Ongeldige datum.'
                      : '',
                  FormHelperTextProps: { sx: { color: 'error.main' } },
                },
              }}
            />
            <Tooltip title="Toon ook plannen zonder einddatum" placement="right">
              <FormControlLabel
                label="Plannen zonder einddatum"
                disabled={!endDateFrom && !endDateTo}
                control={
                  <Checkbox
                    checked={includeZonderEinddatum}
                    onChange={() => onChangeDate('includeZonderEinddatum', !includeZonderEinddatum)}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
              />
            </Tooltip>
          </div>

          {statusses && statusses.length > 0 && (
            <div className="filter-section">
              <Typography variant="overline" color="text.secondary">Overige filters</Typography>
              <FilterCategoryMultiSelect
                label="Status"
                placeholder="Zoek en selecteer een of meerdere statussen"
                noOptionsText="Geen statussen beschikbaar"
                items={statusses}
                selectedValues={selectedFilters.status || []}
                onChange={(values) => handleMultiSelectChange('status', values)}
                chipStyleVariant="default"
              />
            </div>
          )}

          {beoordelingStatusses && beoordelingStatusses.length > 0 && (
            <div className="filter-section">
              {/* <Typography variant="overline" color="text.secondary">Beoordeling</Typography> */}
              <FilterCategoryMultiSelect
                label="Beoordeling"
                placeholder="Zoek en selecteer een of meerdere beoordelingen"
                noOptionsText="Geen beoordelingen beschikbaar"
                items={beoordelingStatusses}
                selectedValues={selectedFilters.beoordeling || []}
                onChange={(values) => handleMultiSelectChange('beoordeling', values)}
                chipStyleVariant="default"
              />
            </div>
          )}

          {disciplines && disciplines.length > 0 && (
            <div className="filter-section">
              {/* <Typography variant="overline" color="text.secondary">Discipline</Typography> */}
              <FilterCategoryMultiSelect
                label="Discipline"
                placeholder="Zoek en selecteer een of meerdere disciplines"
                noOptionsText="Geen disciplines beschikbaar"
                items={disciplines}
                selectedValues={selectedFilters.discipline || []}
                onChange={(values) => handleMultiSelectChange('discipline', values)}
                chipStyleVariant="default"
              />
            </div>
          )}

          {types && types.length > 0 && (
            <div className="filter-section">
              {/* <Typography variant="overline" color="text.secondary">Type</Typography> */}
              <FilterCategoryMultiSelect
                label="Type"
                placeholder="Zoek en selecteer een of meerdere types"
                noOptionsText="Geen types beschikbaar"
                items={types}
                selectedValues={selectedFilters.type || []}
                onChange={(values) => handleMultiSelectChange('type', values)}
                chipStyleVariant="default"
              />
            </div>
          )}

          {organisaties && organisaties.length > 0 && (
            <div className="filter-section">
              {/* <Typography variant="overline" color="text.secondary">Organisatie</Typography> */}
              <FilterCategoryMultiSelect
                label="Organisatie"
                placeholder="Zoek en selecteer een of meerdere organisaties"
                noOptionsText="Geen organisaties beschikbaar"
                items={organisaties}
                selectedValues={selectedFilters.organisatie || []}
                onChange={(values) => handleMultiSelectChange('organisatie', values)}
                chipStyleVariant="default"
              />
            </div>
          )}

          {gemeentes && gemeentes.length > 0 && (
            <div className="filter-section">
              {/* <Typography variant="overline" color="text.secondary">Gemeente</Typography> */}
              <FilterCategoryMultiSelect
                label="Gemeente"
                placeholder="Zoek en selecteer een of meerdere gemeentes"
                noOptionsText="Geen gemeentes beschikbaar"
                items={gemeentes}
                selectedValues={selectedFilters.gemeente || []}
                onChange={(values) => handleMultiSelectChange('gemeente', values)}
                chipStyleVariant="default"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="filter-advanced">
        
        <div className="search">
          <TextField
            fullWidth
            // sx={{ mt: 2 }}
            id="search"
            label="Zoeken in plannen" 
            variant="filled"
            placeholder="Zoeken op nummer, naam, status, beoordeling, type, discipline en organisatie"
            value={selectedSearch ?? ''}
            onChange={onChangeSearchInput}
            // error={!!validationErrors.displayName}
          />
          {!filterOptionOpen && (
            <Tooltip title="Open filter om uitgebreid te zoeken in de plannen">
              <IconButton className='btn-filter-toggle' size="large" onClick={() => setFilterOptionOpen(!filterOptionOpen)}>
                <Icon>filter_list</Icon>
              </IconButton>
            </Tooltip>
          )}

          {filterOptionOpen && (
            <Tooltip title="Sluit het filter">
              <IconButton className='btn-filter-toggle' size="large" onClick={() => setFilterOptionOpen(!filterOptionOpen)}>
                <Icon>filter_list_off</Icon>
              </IconButton>
            </Tooltip>
          )}
          

          {selectedSearch && (
            <IconButton className='btn-search-clear' size="large" onClick={() => onChange('search', '')}>
              <Icon>clear</Icon>
            </IconButton>
          )}

        </div>

        <div className={`filter-options ${filterOptionOpen ? "active" : ""}`}>
          {hasActiveFilters && (
            <Tooltip title="Verwijder filters">
              <IconButton className='btn-filter-clear' size="large" onClick={() => onChange('all', '', true)}>
                <Icon>delete</Icon>
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Sluit filter">
            <IconButton className='btn-filter-close' size="large" onClick={() => setFilterOptionOpen(false)}>
              <Icon>close</Icon>
            </IconButton>
          </Tooltip>

          <div className="container">
            <div className="row">
              <div className="col-xxl-12 col-xl-12 col-lg-12">
                <h5>Uitgebreid zoeken in alle plannen</h5>
              </div>
              <div className="col-xxl-12 col-xl-12 col-lg-12">
                <div className="search">
                  <TextField
                    fullWidth
                    id="search"
                    label="Zoeken in plannen" 
                    variant="filled"
                    placeholder="Zoeken op nummer, naam, status, type, discipline en organisatie"
                    value={selectedSearch ?? ''}
                    onChange={onChangeSearchInput}
                  />

                  { selectedSearch && (
                    <IconButton className='btn-search-clear' size="large" onClick={() => onChange('search', '')}>
                      <Icon>clear</Icon>
                    </IconButton>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="scrollable-area">
            <div className="container">
              <div className="row">
                <div className="col-xxl-12 col-xl-12 col-lg-12">
                  <h5>Startdatum</h5>
                </div>
                <div className="col-xxl-6 col-xl-6 col-lg-12">
                  <DatePicker
                    sx={{ width: '100%' }}
                    label="Van"
                    value={startDateFrom ? dayjs(startDateFrom) : null}
                    onChange={handleDateChange('startDateFrom')}
                    onError={(newError) => setErrorStartDateFrom(newError)}
                    format="DD/MM/YYYY"
                    minDate={MIN_DATE}
                    maxDate={MAX_DATE}
                    slotProps={{
                      textField: {
                        variant: 'filled',
                        helperText:
                          errorStartDateFrom === 'minDate'
                            ? 'Datum mag niet voor 01/01/1970 zijn.'
                            : errorStartDateFrom === 'maxDate'
                            ? 'Datum mag niet na 31/12/2500 zijn.'
                            : errorStartDateFrom === 'invalidDate'
                            ? 'Ongeldige datum.'
                            : '',
                        FormHelperTextProps: { sx: { color: 'error.main' } },
                      },
                    }}
                  />
                </div>
                <div className="col-xxl-6 col-xl-6 col-lg-12">
                  <DatePicker
                    sx={{ width: '100%' }}
                    label="Tot en met"
                    value={startDateTo ? dayjs(startDateTo) : null}
                    onChange={handleDateChange('startDateTo')}
                    onError={(newError) => setErrorStartDateTo(newError)}
                    format="DD/MM/YYYY"
                    minDate={MIN_DATE}
                    maxDate={MAX_DATE}
                    slotProps={{
                      textField: {
                        variant: 'filled',
                        helperText:
                          errorStartDateTo === 'minDate'
                            ? 'Datum mag niet voor 01/01/1970 zijn.'
                            : errorStartDateTo === 'maxDate'
                            ? 'Datum mag niet na 31/12/2500 zijn.'
                            : errorStartDateTo === 'invalidDate'
                            ? 'Ongeldige datum.'
                            : '',
                        FormHelperTextProps: { sx: { color: 'error.main' } },
                      },
                    }}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-xxl-12 col-xl-12 col-lg-12">
                  <h5>Einddatum</h5>
                </div>
                <div className="col-xxl-6 col-xl-6 col-lg-12">
                  <DatePicker
                    sx={{ width: '100%' }}
                    label="Van"
                    value={endDateFrom ? dayjs(endDateFrom) : null}
                    onChange={handleDateChange('endDateFrom')}
                    onError={(newError) => setErrorEndDateFrom(newError)}
                    format="DD/MM/YYYY"
                    minDate={MIN_DATE}
                    maxDate={MAX_DATE}
                    slotProps={{
                      textField: {
                        variant: 'filled',
                        helperText:
                          errorEndDateFrom === 'minDate'
                            ? 'Datum mag niet voor 01/01/1970 zijn.'
                            : errorEndDateFrom === 'maxDate'
                            ? 'Datum mag niet na 31/12/2500 zijn.'
                            : errorEndDateFrom === 'invalidDate'
                            ? 'Ongeldige datum.'
                            : '',
                        FormHelperTextProps: { sx: { color: 'error.main' } },
                      },
                    }}
                  />
                </div>
                <div className="col-xxl-6 col-xl-6 col-lg-12">
                  <DatePicker
                    sx={{ width: '100%' }}
                    label="Tot en met"
                    value={endDateTo ? dayjs(endDateTo) : null}
                    onChange={handleDateChange('endDateTo')}
                    onError={(newError) => setErrorEndDateTo(newError)}
                    format="DD/MM/YYYY"
                    minDate={MIN_DATE}
                    maxDate={MAX_DATE}
                    slotProps={{
                      textField: {
                        variant: 'filled',
                        helperText:
                          errorEndDateTo === 'minDate'
                            ? 'Datum mag niet voor 01/01/1970 zijn.'
                            : errorEndDateTo === 'maxDate'
                            ? 'Datum mag niet na 31/12/2500 zijn.'
                            : errorEndDateTo === 'invalidDate'
                            ? 'Ongeldige datum.'
                            : '',
                        FormHelperTextProps: { sx: { color: 'error.main' } },
                      },
                    }}
                  />
                </div>
                <div className="col-xxl-12 col-xl-12 col-lg-12">
                  <Tooltip title="Toon ook plannen zonder einddatum">
                    <FormControlLabel
                      label="Plannen zonder einddatum"
                      disabled={!endDateFrom && !endDateTo}
                      control={
                        <Checkbox
                          checked={includeZonderEinddatum}
                          onChange={() => onChangeDate('includeZonderEinddatum', !includeZonderEinddatum)}
                          inputProps={{ 'aria-label': 'controlled' }}
                        />
                      }
                    />
                  </Tooltip>
                </div>
              </div>

              { statusses && statusses.length > 0 && (
                <div className="row">
                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <h5>Status</h5>
                  </div>

                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <FilterCategoryMultiSelect
                      label="Status"
                      placeholder="Zoek en selecteer een of meerdere statussen"
                      noOptionsText="Geen statussen beschikbaar"
                      items={statusses}
                      selectedValues={selectedFilters.status || []}
                      onChange={(values) => handleMultiSelectChange('status', values)}
                      chipStyleVariant="filter"
                    />
                  </div>
                </div>
              )}

              { beoordelingStatusses && beoordelingStatusses.length > 0 && (
                <div className="row">
                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <h5>Beoordeling</h5>
                  </div>

                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <FilterCategoryMultiSelect
                      label="Beoordeling"
                      placeholder="Zoek en selecteer een of meerdere beoordelingen"
                      noOptionsText="Geen beoordelingen beschikbaar"
                      items={beoordelingStatusses}
                      selectedValues={selectedFilters.beoordeling || []}
                      onChange={(values) => handleMultiSelectChange('beoordeling', values)}
                      chipStyleVariant="filter"
                    />
                  </div>
                </div>
              )}

              { disciplines && disciplines.length > 0 && (
                <div className="row">
                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <h5>Discipline</h5>
                  </div>

                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <FilterCategoryMultiSelect
                      label="Discipline"
                      placeholder="Zoek en selecteer een of meerdere disciplines"
                      noOptionsText="Geen disciplines beschikbaar"
                      items={disciplines}
                      selectedValues={selectedFilters.discipline || []}
                      onChange={(values) => handleMultiSelectChange('discipline', values)}
                      chipStyleVariant="filter"
                    />
                  </div>
                </div>
              )}

              { types && types.length > 0 && (
                <div className="row">
                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <h5>Type</h5>
                  </div>

                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <FilterCategoryMultiSelect
                      label="Type"
                      placeholder="Zoek en selecteer een of meerdere types"
                      noOptionsText="Geen types beschikbaar"
                      items={types}
                      selectedValues={selectedFilters.type || []}
                      onChange={(values) => handleMultiSelectChange('type', values)}
                      chipStyleVariant="filter"
                    />
                  </div>
                </div>
              )}

              { organisaties && organisaties.length > 0 && (
                <div className="row">
                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <h5>Organisatie</h5>
                  </div>

                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <FilterCategoryMultiSelect
                      label="Organisatie"
                      placeholder="Zoek en selecteer een of meerdere organisaties"
                      noOptionsText="Geen organisaties beschikbaar"
                      items={organisaties}
                      selectedValues={selectedFilters.organisatie || []}
                      onChange={(values) => handleMultiSelectChange('organisatie', values)}
                      chipStyleVariant="filter"
                    />
                  </div>
                </div>
              )}


            </div>
          </div>

          <div className="container">
            <div className="row">
              <div className="col-xxl-12 col-xl-12 col-lg-12">
                <p className="note right">Let op! Alle filters worden automatisch toegepast</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      { loading && (
        <ProgressBar />
      )}

    </>
  );
};

export default FilterPlannen;